import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ENTER, SPACE } from '@angular/cdk/keycodes';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  ViewEncapsulation,
} from '@angular/core';
import { classnames } from '@fmnts/common';
import { FmntsIconsModule } from '@fmnts/components/icons';
import { isNotNullish, maybeOptionHasValue } from '@fmnts/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faSort, faSortDown, faSortUp } from '@fortawesome/pro-solid-svg-icons';
import { merge, Subscription } from 'rxjs';
import {
  FmntsSortDirective,
  SORT_DEFAULT_OPTIONS,
  Sortable,
  SortDefaultOptions,
  SortDirection,
  SortHeaderArrowPosition,
} from './sort.directive';

/**
 * Valid positions for the arrow to be in for its opacity and translation. If the state is a
 * sort direction, the position of the arrow will be above/below and opacity 0. If the state is
 * hint, the arrow will be in the center with a slight opacity. Active state means the arrow will
 * be fully opaque in the center.
 */
type ArrowViewState = SortDirection | 'hint' | 'active';

/**
 * States describing the arrow's animated position (animating fromState to toState).
 * If the fromState is not defined, there will be no animated transition to the toState.
 */
interface ArrowViewStateTransition {
  fromState?: ArrowViewState;
  toState?: ArrowViewState;
}

/** Column definition associated with a `SortHeader`. */
interface SortHeaderColumnDef {
  name: string;
}

/**
 * Applies sorting behavior (click to change sort) and styles to an element, including an
 * arrow to display the current sort direction.
 *
 * Must be provided with an id and contained within a parent Sort directive.
 */
@Component({
  selector: 'fmnts-sort-header, [fmnts-sort-header]',
  styleUrls: ['./sort-header.component.scss'],
  templateUrl: './sort-header.component.html',
  exportAs: 'fmnts-sort-header',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    '(click)': '_handleClick()',
    '(keydown)': '_handleKeydown($event)',
    '(mouseenter)': '_setIndicatorHintVisible(true)',
    '(mouseleave)': '_setIndicatorHintVisible(false)',
    '[attr.aria-sort]': '_getAriaSortAttribute()',
  },
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FmntsIconsModule],
})
export class FmntsSortHeaderComponent
  implements Sortable, OnDestroy, OnInit, AfterViewInit
{
  @HostBinding('class.fmnts-sort-header') componentClass = 'fmnts-sort-header';

  private _rerenderSubscription: Subscription | undefined;

  /**
   * Flag set to true when the indicator should be displayed while the sort is not active. Used to
   * provide an affordance that the header is sortable by showing on focus and hover.
   */
  _showIndicatorHint = false;

  /**
   * The view transition state of the arrow (translation/ opacity) - indicates its `from` and `to`
   * position through the animation. If animations are currently disabled, the fromState is removed
   * so that there is no animation displayed.
   */
  protected _viewState: ArrowViewStateTransition = {};

  /** The direction the arrow should be facing according to the current state. */
  protected _arrowDirection: SortDirection = null;

  /**
   * Whether the view state animation should show the transition between the `from` and `to` states.
   */
  _disableViewStateAnimation = false;

  /**
   * ID of this sort header.
   */
  @Input('fmnts-sort-header') id!: Sortable['id'];

  /** Sets the position of the arrow that displays when sorted. */
  @Input() arrowPosition: SortHeaderArrowPosition = 'after';

  /** Overrides the sort start value of the containing Sort for this Sortable. */
  @Input() start: Sortable['start'];

  /** whether the sort header is disabled. */
  @Input() get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
  }
  private _disabled = false;

  /**
   * Description applied to FmntsSortHeader's button element with aria-describedby. This text should
   * describe the action that will occur when the user clicks the sort header.
   */
  @Input()
  get sortActionDescription(): string {
    return this._sortActionDescription;
  }
  set sortActionDescription(value: string) {}
  // Default the action description to "Sort" because it's better than nothing.
  // Without a description, the button's label comes from the sort header text content,
  // which doesn't give any indication that it performs a sorting operation.
  private _sortActionDescription = 'Sort';

  /** Overrides the disable clear value of the containing Sort for this Sortable. */
  @Input() get disableClear(): Sortable['disableClear'] {
    return this._disableClear;
  }
  set disableClear(value: BooleanInput) {
    this._disableClear = isNotNullish(value)
      ? coerceBooleanProperty(value)
      : undefined;
  }
  private _disableClear: Sortable['disableClear'];

  private readonly _iconSortAsc = faSortUp;
  private readonly _iconSortDesc = faSortDown;
  private readonly _iconSort = faSort;

  get iconName(): IconProp {
    switch (this._direction()) {
      case 'asc':
        return this._iconSortAsc;
      case 'desc':
        return this._iconSortDesc;
      default:
        return this._iconSort;
    }
  }

  @HostBinding('class')
  protected get hostClasses(): string {
    const direction = this._direction();
    return classnames([
      this._isDisabled() && `${this.componentClass}--disabled`,
      direction && `${this.componentClass}--${direction}`,
    ]);
  }

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    public _sort: FmntsSortDirective,
    @Inject('SORT_HEADER_COLUMN_DEF')
    @Optional()
    protected _columnDef: SortHeaderColumnDef | null,
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    @Optional()
    @Inject(SORT_DEFAULT_OPTIONS)
    protected defaultOptions: SortDefaultOptions | null,
  ) {
    if (defaultOptions?.arrowPosition) {
      this.arrowPosition = defaultOptions?.arrowPosition;
    }

    this._handleStateChanges();
  }

  ngOnInit(): void {
    if (!this.id && this._columnDef) {
      this.id = this._columnDef.name;
    }

    // Initialize the direction of the arrow and set the view state to be immediately that state.
    this._updateArrowDirection();
    this._setAnimationTransitionState({
      toState: this._isSorted() ? 'active' : this._arrowDirection,
    });

    this._sort.register(this);
  }

  ngAfterViewInit(): void {
    // We use the focus monitor because we also want to style
    // things differently based on the focus origin.
    this._focusMonitor.monitor(this._elementRef, true).subscribe((origin) => {
      const newState = !!origin;
      if (newState !== this._showIndicatorHint) {
        this._setIndicatorHintVisible(newState);
        this._changeDetectorRef.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this._focusMonitor.stopMonitoring(this._elementRef);
    this._sort.deregister(this);
    this._rerenderSubscription?.unsubscribe();
  }

  /**
   * Sets the "hint" state such that the arrow will be semi-transparently displayed as a hint to the
   * user showing what the active sort will become. If set to false, the arrow will fade away.
   */
  protected _setIndicatorHintVisible(visible: boolean): void {
    // No-op if the sort header is disabled - should not make the hint visible.
    if (this._isDisabled() && visible) {
      return;
    }

    this._showIndicatorHint = visible;

    if (!this._isSorted()) {
      this._updateArrowDirection();
      if (this._showIndicatorHint) {
        this._setAnimationTransitionState({
          fromState: this._arrowDirection,
          toState: 'hint',
        });
      } else {
        this._setAnimationTransitionState({
          fromState: 'hint',
          toState: this._arrowDirection,
        });
      }
    }
  }

  /**
   * Sets the animation transition view state for the arrow's position and opacity. If the
   * `disableViewStateAnimation` flag is set to true, the `fromState` will be ignored so that
   * no animation appears.
   */
  protected _setAnimationTransitionState(
    viewState: ArrowViewStateTransition,
  ): void {
    this._viewState = viewState || {};

    // If the animation for arrow position state (opacity/translation) should be disabled,
    // remove the fromState so that it jumps right to the toState.
    if (this._disableViewStateAnimation) {
      this._viewState = { toState: viewState.toState };
    }
  }

  /** Triggers the sort on this sort header and removes the indicator hint. */
  protected _toggleOnInteraction(): void {
    this._sort.sort(this);

    // Do not show the animation if the header was already shown in the right position.
    if (
      this._viewState.toState === 'hint' ||
      this._viewState.toState === 'active'
    ) {
      this._disableViewStateAnimation = true;
    }
  }

  protected _handleClick(): void {
    if (!this._isDisabled()) {
      this._sort.sort(this);
    }
  }

  protected _handleKeydown(event: KeyboardEvent): void {
    if (
      !this._isDisabled() &&
      (event.keyCode === SPACE || event.keyCode === ENTER)
    ) {
      event.preventDefault();
      this._toggleOnInteraction();
    }
  }

  /** Whether this SortHeader is currently sorted in either ascending or descending order. */
  protected _isSorted(): boolean {
    return (
      this._sort.active === this.id && maybeOptionHasValue(this._sort.direction)
    );
  }

  protected _direction(): SortDirection {
    return this._isSorted() ? this._sort.direction : null;
  }

  /** Returns the animation state for the arrow direction (indicator and pointers). */
  protected _getArrowDirectionState(): string {
    return `${this._isSorted() ? 'active-' : ''}${this._arrowDirection}`;
  }

  /** Returns the arrow position state (opacity, translation). */
  protected _getArrowViewState(): string {
    const fromState = this._viewState.fromState;
    return (fromState ? `${fromState}-to-` : '') + this._viewState.toState;
  }

  /**
   * Updates the direction the arrow should be pointing. If it is not sorted, the arrow should be
   * facing the start direction. Otherwise if it is sorted, the arrow should point in the currently
   * active sorted direction. The reason this is updated through a function is because the direction
   * should only be changed at specific times - when deactivated but the hint is displayed and when
   * the sort is active and the direction changes. Otherwise the arrow's direction should linger
   * in cases such as the sort becoming deactivated but we want to animate the arrow away while
   * preserving its direction, even though the next sort direction is actually different and should
   * only be changed once the arrow displays again (hint or activation).
   */
  protected _updateArrowDirection(): void {
    this._arrowDirection = this._isSorted()
      ? this._sort.direction
      : (this.start ?? this._sort.start);
  }

  protected _isDisabled(): boolean {
    return this._sort.sortDisabled || this.disabled;
  }

  /**
   * Gets the aria-sort attribute that should be applied to this sort header. If this header
   * is not sorted, returns null so that the attribute is removed from the host element. Aria spec
   * says that the aria-sort property should only be present on one header at a time, so removing
   * ensures this is true.
   */
  protected _getAriaSortAttribute(): string {
    if (!this._isSorted()) {
      return 'none';
    }

    return this._sort.direction === 'asc' ? 'ascending' : 'descending';
  }

  /** Whether the arrow inside the sort header should be rendered. */
  protected _renderArrow(): boolean {
    return !this._isDisabled() || this._isSorted();
  }

  /** Handles changes in the sorting state. */
  private _handleStateChanges() {
    this._rerenderSubscription = merge(
      this._sort.sortChange,
      this._sort._stateChanges,
    ).subscribe(() => {
      if (this._isSorted()) {
        this._updateArrowDirection();

        // Do not show the animation if the header was already shown in the right position.
        if (
          this._viewState.toState === 'hint' ||
          this._viewState.toState === 'active'
        ) {
          this._disableViewStateAnimation = true;
        }

        this._setAnimationTransitionState({
          fromState: this._arrowDirection,
          toState: 'active',
        });
        this._showIndicatorHint = false;
      }

      // If this header was recently active and now no longer sorted, animate away the arrow.
      if (
        !this._isSorted() &&
        this._viewState &&
        this._viewState.toState === 'active'
      ) {
        this._disableViewStateAnimation = false;
        this._setAnimationTransitionState({
          fromState: 'active',
          toState: this._arrowDirection,
        });
      }

      this._changeDetectorRef.markForCheck();
    });
  }
}
